import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Upload, Search, Pencil, Archive, UserPlus, Download, Trash2, Film, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, imgUrl, BACKEND } from "@/lib/api";
import { PageHeader, Pager, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const Hint = ({ children }) => <p className="mt-0.5 text-[10px] leading-snug text-slate-600">{children}</p>;


const EMPTY = {
  sku: "", name: "", short_description: "", description: "", brand: "", category_id: "",
  meta_title: "", meta_description: "", keywords: "",
  images: [], videos: [], marketplace_image: "", purchase_cost: 0, agent_cost: 0, mrp: 0, suggested_price: 0, min_price: 0,
  gst: 18, hsn: "", weight: 0.5, stock: 0, reorder_level: 10, status: "draft",
  supplier: "", supplier_sku: "", return_policy: "7-day easy returns", warranty: "", tags: [],
};

function ProductForm({ initial, categories, onSave, busy }) {
  const [p, setP] = useState(initial);
  const [imgInput, setImgInput] = useState("");
  const [uploads, setUploads] = useState([]); // [{id, name, pct, failed}]
  const [brokenImages, setBrokenImages] = useState(new Set());
  const [brokenThumb, setBrokenThumb] = useState(false);
  const fileRef = useRef();
  const videoRef = useRef();
  const set = (k, v) => setP({ ...p, [k]: v });
  const [aiBusy, setAiBusy] = useState(false);

  const generateAI = async () => {
    if (!p.name.trim()) { toast.error("Enter the product name first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/products/ai-autofill", { name: p.name, sku: p.sku, brand: p.brand, category_id: p.category_id });
      if (data.fields) {
        setP((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(data.fields).filter(([, v]) => v !== "" && v != null)) }));
        toast.success("AI generated HSN, GST, descriptions & SEO — review and adjust, then add prices/images");
      }
    } catch (e) { toast.error(errMsg(e)); }
    setAiBusy(false);
  };

  const upload = async (file) => {
    const isVideo = file.type.startsWith("video/") || /\.(mp4|webm|mov|m4v)$/i.test(file.name);
    const okImages = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/bmp"];
    if (isVideo) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`"${file.name}" is ${(file.size / 1048576).toFixed(1)} MB — videos max 50 MB`);
        return;
      }
    } else {
      if (!okImages.includes(file.type)) {
        toast.error(`"${file.name}" is ${file.type || "unknown type"} — images: JPG/PNG/WEBP/GIF/BMP (max 5 MB) · videos: MP4/WEBM/MOV (max 50 MB)`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`"${file.name}" is ${(file.size / 1048576).toFixed(1)} MB — images max 5 MB`);
        return;
      }
    }
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setUploads((prev) => [...prev, { id: uid, name: file.name, pct: 0, failed: false }]);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          setUploads((prev) => prev.map((u) => (u.id === uid ? { ...u, pct } : u)));
        },
      });
      if (data.url && data.kind === "video") {
        setP((prev) => ({ ...prev, videos: [...(prev.videos || []), data.url] }));
        toast.success("Item video uploaded");
      } else if (data.url) {
        setP((prev) => ({ ...prev, images: [...prev.images, data.url], marketplace_image: prev.marketplace_image || data.thumb_url || "" }));
        toast.success(data.note || "Image uploaded & auto-adjusted");
      } else {
        setUploads((prev) => prev.map((u) => (u.id === uid ? { ...u, failed: true } : u)));
        toast.error(data.error || "Upload failed");
        return;
      }
      setUploads((prev) => prev.filter((u) => u.id !== uid));
    } catch (e) {
      setUploads((prev) => prev.map((u) => (u.id === uid ? { ...u, failed: true } : u)));
      toast.error(errMsg(e));
    }
  };

  const uploadMany = (files) => {
    const list = Array.from(files || []);
    if (list.length > 5) toast.error(`You selected ${list.length} files — upload up to 5 images at a time (first 5 used)`);
    list.slice(0, 5).forEach((f) => upload(f));
  };

  return (
    <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs text-slate-400">SKU *</Label>
          <Input data-testid="product-sku-input" value={p.sku} onChange={(e) => set("sku", e.target.value)} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
          <Hint>Unique product code (e.g. SW-X1). Used for marketplace listings — cannot repeat.</Hint></div>
        <div><Label className="text-xs text-slate-400">Name *</Label>
          <Input data-testid="product-name-input" value={p.name} onChange={(e) => set("name", e.target.value)} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
          <Hint>Customer-facing title shown on stores & marketplaces.</Hint></div>
        <div><Label className="text-xs text-slate-400">Brand</Label>
          <Input data-testid="product-brand-input" value={p.brand} onChange={(e) => set("brand", e.target.value)} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
          <Hint>Optional brand/manufacturer name.</Hint></div>
        <div><Label className="text-xs text-slate-400">Category</Label>
          <Select value={p.category_id} onValueChange={(v) => set("category_id", v)}>
            <SelectTrigger data-testid="product-category-select" className="mt-1 border-slate-700 bg-slate-900 text-slate-100"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Hint>Groups the product in agent baskets & store categories.</Hint></div>
        {[["purchase_cost", "Purchase Cost ₹ (incl. GST)", "What you pay the supplier."], ["agent_cost", "Agent Cost ₹ (incl. GST)", "Base/partner price agents pay you per sale."], ["mrp", "MRP ₹ (incl. GST)", "Printed maximum retail price shown as strike-through."], ["suggested_price", "Suggested Price ₹ (incl. GST)", "Recommended selling price for agents (they can change it)."], ["min_price", "Min Selling Price ₹ (incl. GST)", "Lowest price agents are allowed to sell at."]].map(([k, label, hint]) => (
          <div key={k}><Label className="text-xs text-slate-400">{label}</Label>
            <Input data-testid={`product-${k}-input`} type="number" value={p[k]} onChange={(e) => set(k, Number(e.target.value))} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
            <Hint>{hint}</Hint></div>
        ))}
        <div><Label className="text-xs text-slate-400">GST %</Label>
          <Select value={String(p.gst)} onValueChange={(v) => set("gst", Number(v))}>
            <SelectTrigger data-testid="product-gst-select" className="mt-1 border-slate-700 bg-slate-900 text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
              {[0, 5, 12, 18, 28].map((g) => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}
            </SelectContent>
          </Select></div>
        <div><Label className="text-xs text-slate-400">HSN</Label>
          <Input data-testid="product-hsn-input" value={p.hsn} onChange={(e) => set("hsn", e.target.value)} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
          <Hint>4–8 digit GST tax code (e.g. 8517 for electronics).</Hint></div>
        <div><Label className="text-xs text-slate-400">Shipping weight (kg)</Label>
          <Input data-testid="product-weight-input" type="number" step="0.01" min="0.05" value={p.weight} onChange={(e) => set("weight", Number(e.target.value))} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
          <Hint>Packed parcel weight — drives exact shipping charge in the agent profit calculator.</Hint></div>
        <p className="col-span-2 rounded-lg bg-slate-900/60 px-3 py-2 text-[10px] text-slate-500" data-testid="gst-breakup-hint">
          All prices are GST-inclusive. Suggested price ₹{(p.suggested_price || 0).toLocaleString("en-IN")} @ {p.gst}% GST →
          taxable ₹{((p.suggested_price || 0) / (1 + (p.gst || 0) / 100)).toFixed(2)} + GST ₹{((p.suggested_price || 0) - (p.suggested_price || 0) / (1 + (p.gst || 0) / 100)).toFixed(2)}
        </p>
        <div><Label className="text-xs text-slate-400">Stock</Label>
          <Input data-testid="product-stock-input" type="number" value={p.stock} onChange={(e) => set("stock", Number(e.target.value))} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
          <Hint>Total units available across all agents.</Hint></div>
        <div><Label className="text-xs text-slate-400">Status</Label>
          <Select value={p.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="product-status-select" className="mt-1 border-slate-700 bg-slate-900 text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
              {["draft", "active", "out_of_stock", "archived", "blocked"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select></div>
      </div>
      <button type="button" data-testid="product-ai-generate-btn" onClick={generateAI} disabled={aiBusy}
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-300 transition-opacity hover:opacity-80 disabled:opacity-50">
        <Sparkles size={13} /> {aiBusy ? "AI is writing…" : "Generate with AI — HSN, GST, descriptions & SEO (fill SKU, name, brand first)"}
      </button>
      <div><Label className="text-xs text-slate-400">Short description</Label>
        <Input data-testid="product-short-desc-input" value={p.short_description} onChange={(e) => set("short_description", e.target.value)} className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
        <Hint>1–2 lines shown on product cards and marketplace previews.</Hint></div>
      <div><Label className="text-xs text-slate-400">Long description</Label>
        <textarea data-testid="product-long-desc-input" value={p.description || ""} onChange={(e) => set("description", e.target.value)} rows={3}
          className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100" />
        <Hint>Full product story on the detail page. Leave empty to auto-generate from name/brand/category on save.</Hint></div>
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3" data-testid="seo-section">
        <Label className="text-xs text-slate-400">SEO (Google / search)</Label>
        <Hint>Auto-generated on save when left empty — edit anytime. Title ≤ 60 chars, description ≤ 155 chars.</Hint>
        <div className="mt-2 space-y-2">
          <div>
            <Input data-testid="product-meta-title-input" value={p.meta_title || ""} maxLength={60} onChange={(e) => set("meta_title", e.target.value)}
              placeholder="Meta title (e.g. Product Name — Buy Online at Best Price)" className="border-slate-700 bg-slate-900 text-slate-100" />
            <Hint>{(p.meta_title || "").length}/60</Hint>
          </div>
          <div>
            <textarea data-testid="product-meta-desc-input" value={p.meta_description || ""} maxLength={155} rows={2} onChange={(e) => set("meta_description", e.target.value)}
              placeholder="Meta description shown in Google results" className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100" />
            <Hint>{(p.meta_description || "").length}/155</Hint>
          </div>
          <div>
            <Input data-testid="product-keywords-input" value={p.keywords || ""} onChange={(e) => set("keywords", e.target.value)}
              placeholder="Keywords, comma separated" className="border-slate-700 bg-slate-900 text-slate-100" />
            <Hint>Comma-separated search keywords (e.g. men watch, leather strap, buy watch online).</Hint>
          </div>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs text-slate-400">Images</Label>
          {brokenImages.size > 0 && (
            <button
              type="button"
              data-testid="remove-broken-images-btn"
              onClick={() => {
                const cleaned = p.images.filter((_, idx) => !brokenImages.has(idx));
                setP((prev) => ({
                  ...prev,
                  images: cleaned,
                  marketplace_image: brokenThumb ? "" : prev.marketplace_image,
                }));
                setBrokenImages(new Set());
                toast.success("Removed missing image links. Upload fresh images or save.");
              }}
              className="text-[11px] font-medium text-rose-400 hover:text-rose-300 underline"
            >
              Remove {brokenImages.size} missing image{brokenImages.size > 1 ? "s" : ""}
            </button>
          )}
        </div>
        <p className="mt-0.5 text-[10px] text-slate-500" data-testid="image-guidelines">
          Upload any image — we auto-adjust it: resized to max <b>1200×1200px</b>, optimized JPEG/PNG, plus a pure-white <b>1000×1000 marketplace thumbnail</b> (Amazon-ready main image) is generated automatically. Images: <b>JPG, JPEG, PNG, WEBP, GIF, BMP · max 5 MB each · up to 5 at once</b>. First image = MAIN thumbnail. You can also paste an image URL.
        </p>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {p.images.map((u, i) => {
            const isBroken = brokenImages.has(i);
            return (
              <div key={i} className={`group relative rounded-lg border ${isBroken ? "border-rose-500/60 bg-rose-950/20" : "border-slate-700"}`}>
                {isBroken ? (
                  <div className="flex h-16 w-16 flex-col items-center justify-center p-1 text-center">
                    <span className="text-[14px]">⚠️</span>
                    <span className="text-[8px] font-semibold text-rose-400 leading-tight">Missing file</span>
                  </div>
                ) : (
                  <img
                    src={imgUrl(u)}
                    alt=""
                    onError={() => {
                      setBrokenImages((prev) => new Set([...prev, i]));
                    }}
                    className="h-16 w-16 rounded-lg object-cover"
                  />
                )}
                {i === 0 && !isBroken && (
                  <span className="absolute -top-1.5 left-1 rounded bg-orange-500 px-1 text-[8px] font-bold text-white">MAIN</span>
                )}
                {isBroken && (
                  <span className="absolute -top-1.5 left-1 rounded bg-rose-600 px-1 text-[8px] font-bold text-white">404</span>
                )}
                <button
                  type="button"
                  data-testid={`remove-image-${i}`}
                  onClick={() => {
                    set("images", p.images.filter((_, j) => j !== i));
                    setBrokenImages((prev) => {
                      const next = new Set();
                      for (const idx of prev) {
                        if (idx < i) next.add(idx);
                        else if (idx > i) next.add(idx - 1);
                      }
                      return next;
                    });
                  }}
                  className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[9px] text-white hover:bg-rose-600 shadow"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex gap-2">
          <Input data-testid="product-image-url-input" value={imgInput} onChange={(e) => setImgInput(e.target.value)} placeholder="Paste image URL"
            className="border-slate-700 bg-slate-900 text-xs text-slate-100" />
          <Button data-testid="add-image-url-btn" size="sm" variant="outline" className="border-slate-700 text-slate-300"
            onClick={() => { if (imgInput.trim()) { set("images", [...p.images, imgInput.trim()]); setImgInput(""); } }}>Add</Button>
          <Button data-testid="upload-image-btn" size="sm" variant="outline" className="border-slate-700 text-slate-300" onClick={() => fileRef.current.click()}>
            <Upload size={13} /> Upload image
          </Button>
          <Button data-testid="upload-video-btn" size="sm" variant="outline" className="border-slate-700 text-slate-300" onClick={() => videoRef.current.click()}>
            <Film size={13} /> Item video
          </Button>
          <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.gif,.bmp,image/jpeg,image/png,image/webp,image/gif,image/bmp" className="hidden" data-testid="image-file-input"
            onChange={(e) => { uploadMany(e.target.files); e.target.value = ""; }} />
          <input ref={videoRef} type="file" accept=".mp4,.webm,.mov,.m4v,video/mp4,video/webm,video/quicktime" className="hidden" data-testid="video-file-input"
            onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ""; }} />
        </div>
        {uploads.length > 0 && (
          <div className="mt-2 space-y-1.5" data-testid="upload-progress-list">
            {uploads.map((u) => (
              <div key={u.id} data-testid={`upload-progress-${u.id}`} className="rounded-lg border border-slate-800 bg-slate-900/60 px-2.5 py-1.5">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="max-w-[70%] truncate text-slate-400">{u.name}</span>
                  <span className={`font-mono font-semibold ${u.failed ? "text-rose-400" : "text-emerald-400"}`}>{u.failed ? "failed" : `${u.pct}%`}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-800">
                  <div className={`h-full rounded-full transition-[width] duration-200 ${u.failed ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${u.failed ? 100 : u.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
        {p.marketplace_image && (
          <div className={`mt-2 flex items-center justify-between gap-2 rounded-lg border p-2 ${brokenThumb ? "border-rose-500/50 bg-rose-950/20" : "border-slate-800 bg-slate-900/60"}`} data-testid="marketplace-thumb-preview">
            <div className="flex items-center gap-2">
              {brokenThumb ? (
                <div className="flex h-14 w-14 flex-col items-center justify-center rounded border border-rose-500/50 bg-slate-900 text-center">
                  <span className="text-[12px]">⚠️</span>
                  <span className="text-[7px] text-rose-400 font-bold">Missing</span>
                </div>
              ) : (
                <img
                  src={imgUrl(p.marketplace_image)}
                  alt=""
                  onError={() => setBrokenThumb(true)}
                  className="h-14 w-14 rounded border border-slate-700 object-cover"
                />
              )}
              <p className="text-[10px] text-slate-500">
                Auto-generated pure-white 1000×1000 marketplace thumbnail (used as the Amazon/Flipkart main image).
              </p>
            </div>
            {brokenThumb && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-rose-500/50 text-[10px] text-rose-300 hover:bg-rose-950/40"
                onClick={() => {
                  setP((prev) => ({ ...prev, marketplace_image: "" }));
                  setBrokenThumb(false);
                  toast.success("Removed broken thumbnail. Upload a fresh image or save to auto-regenerate.");
                }}
              >
                Clear thumbnail
              </Button>
            )}
          </div>
        )}
        {(p.videos || []).length > 0 && (
          <div className="mt-2 space-y-1.5" data-testid="product-videos-list">
            {(p.videos || []).map((v, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/60 px-2.5 py-1.5">
                <span className="flex items-center gap-1.5 text-[11px] text-slate-300"><Film size={12} className="text-slate-500" /> Item video {i + 1}</span>
                <span className="flex items-center gap-2">
                  <video src={imgUrl(v)} className="h-8 w-14 rounded object-cover" muted />
                  <button data-testid={`remove-video-${i}`} onClick={() => set("videos", p.videos.filter((_, j) => j !== i))}
                    className="rounded p-0.5 text-slate-500 hover:text-rose-400">×</button>
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="mt-1.5 text-[10px] text-slate-600" data-testid="video-guidelines">
          Item video (optional): <b>MP4, WEBM or MOV · max 50 MB</b> · recommended ≤30 seconds, 720p–1080p, product close-up.
        </p>
      </div>
      <Button data-testid="product-save-btn" disabled={busy} onClick={() => onSave(p)} className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600">
        {busy ? "Saving…" : "Save product"}
      </Button>
    </div>
  );
}

export default function Products() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [ptype, setPtype] = useState("");
  const [approval, setApproval] = useState("");
  const [dialog, setDialog] = useState(null); // {mode:'create'|'edit'|'import'|'assign', product}
  const [selected, setSelected] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [assignAgents, setAssignAgents] = useState([]);
  const importRef = useRef();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-products", page, search, status, category, ptype, approval],
    queryFn: async () => (await api.get(`/admin/products?page=${page}&limit=20&search=${encodeURIComponent(search)}&status=${status}&category_id=${category}&product_type=${ptype}&approval_status=${approval}`)).data,
  });
  const { data: categories } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/admin/categories")).data });
  const { data: agents } = useQuery({ queryKey: ["agents-mini"], queryFn: async () => (await api.get("/admin/agents?limit=100")).data });
  const agentNames = Object.fromEntries((agents?.items || []).map((a) => [a.id, a.business_name]));

  const save = useMutation({
    mutationFn: async ({ mode, payload, id }) => mode === "edit" ? api.put(`/admin/products/${id}`, payload) : api.post("/admin/products", payload),
    onSuccess: () => { toast.success("Product saved"); setDialog(null); qc.invalidateQueries({ queryKey: ["admin-products"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const approveMut = useMutation({
    mutationFn: async (id) => api.post(`/admin/products/${id}/approve`, {}),
    onSuccess: () => { toast.success("Product approved & live"); qc.invalidateQueries({ queryKey: ["admin-products"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const rejectMut = useMutation({
    mutationFn: async ({ id, reason }) => api.post(`/admin/products/${id}/reject`, { reason }),
    onSuccess: () => { toast.success("Product rejected"); qc.invalidateQueries({ queryKey: ["admin-products"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const archive = useMutation({
    mutationFn: async (id) => api.delete(`/admin/products/${id}`),
    onSuccess: () => { toast.success("Product archived"); qc.invalidateQueries({ queryKey: ["admin-products"] }); },
  });
  const hardDelete = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/products/${id}/permanent`)).data,
    onSuccess: (d) => { toast.success(`Deleted ${d.deleted} permanently (removed from ${d.cascade.agent_products} baskets, ${d.cascade.store_products} stores)`); setDialog(null); qc.invalidateQueries({ queryKey: ["admin-products"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const doImport = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/products/bulk-import", fd);
      setImportResult(data);
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      toast.success(`Imported ${data.imported} of ${data.total}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const doAssign = useMutation({
    mutationFn: async () => (await api.post("/admin/products/assign", { product_ids: dialog?.mode === "assign" ? selected : [dialog.product.id], agent_ids: assignAgents })).data,
    onSuccess: (d) => { toast.success(`Assigned to agents (${d.added} new)`); setDialog(null); setSelected([]); setAssignAgents([]); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const downloadErrors = () => {
    const rows = ["row,sku,error", ...(importResult?.errors || []).map((e) => `${e.row},${e.sku},"${e.error.replace(/"/g, "'")}"`)];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "import_errors.csv";
    a.click();
  };

  return (
    <div data-testid="admin-products-page">
      <PageHeader title="Master Catalog" sub={`${data?.total ?? 0} products · server-side search, filters and pagination`} testid="products-title">
        {selected.length > 0 && (
          <Button data-testid="bulk-assign-btn" variant="outline" onClick={() => setDialog({ mode: "assign" })}
            className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10">
            <UserPlus size={14} /> Assign {selected.length}
          </Button>
        )}
        <Button data-testid="bulk-import-btn" variant="outline" onClick={() => { setImportResult(null); setDialog({ mode: "import" }); }}
          className="border-slate-700 text-slate-300 hover:bg-slate-800"><Upload size={14} /> Bulk Import</Button>
        <Button data-testid="add-product-btn" onClick={() => setDialog({ mode: "create" })} className="bg-orange-500 text-white hover:bg-orange-600"><Plus size={14} /> Product</Button>
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-52">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="product-search-input" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search name, SKU, brand…" className="border-slate-700 bg-[#111827] pl-9 text-slate-100 placeholder:text-slate-600" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="product-status-filter" className="w-40 border-slate-700 bg-[#111827] text-slate-200"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent className="border-slate-700 bg-[#111827] text-slate-200">
            {["all", "active", "draft", "out_of_stock", "archived", "blocked"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={(v) => { setCategory(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="product-category-filter" className="w-44 border-slate-700 bg-[#111827] text-slate-200"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent className="border-slate-700 bg-[#111827] text-slate-200">
            <SelectItem value="all">All categories</SelectItem>
            {(categories || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mb-3 flex flex-wrap gap-1.5" data-testid="product-type-filter">
        {[["", "", "All"], ["ADMIN_PRODUCT", "", "Admin products"], ["AGENT_PRODUCT", "", "Agent products"], ["", "PENDING", "Pending approval"]].map(([t, ap, label]) => (
          <button key={label} data-testid={`type-filter-${label.toLowerCase().replace(/ /g, "-")}`}
            onClick={() => { setPtype(t); setApproval(ap); setPage(1); }}
            className={`rounded-full border px-3 py-1 text-[10px] font-semibold ${ptype === t && approval === ap ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-slate-700 text-slate-400 hover:bg-slate-800"}`}>
            {label}
          </button>
        ))}
      </div>

      {data?.missing_images > 0 && (
        <p data-testid="missing-images-warning" className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-300">
          {data.missing_images} product(s) have no image — they cannot get a marketplace thumbnail or a proper listing. Edit the product and upload an image.
        </p>
      )}

      {isLoading ? <p className="text-sm text-slate-500">Loading…</p> : !data?.items?.length ? <EmptyState title="No products found" sub="Create one or bulk import a CSV/XLSX." /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-800 bg-[#111827]">
          <table className="w-full text-left text-xs" data-testid="products-table">
            <thead>
              <tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-3">
                  <Checkbox data-testid="select-all-products" checked={selected.length === data.items.length}
                    onCheckedChange={(c) => setSelected(c ? data.items.map((p) => p.id) : [])} />
                </th>
                <th className="px-3 py-3">Product</th>
                <th className="px-3 py-3">Category</th>
                <th className="px-3 py-3">Agent Cost</th>
                <th className="px-3 py-3">Suggested</th>
                <th className="px-3 py-3">Min Price</th>
                <th className="px-3 py-3">Stock</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.sku}`} className="border-b border-slate-800/50 hover:bg-slate-900/40">
                  <td className="px-3 py-2">
                    <Checkbox data-testid={`select-product-${p.sku}`} checked={selected.includes(p.id)}
                      onCheckedChange={(c) => setSelected(c ? [...selected, p.id] : selected.filter((x) => x !== p.id))} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2.5">
                      {p.images?.[0] ? <img src={imgUrl(p.images[0])} alt="" className="h-9 w-9 rounded-lg object-cover" /> : <div data-testid={`no-image-${p.sku}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-rose-500/40 bg-rose-500/10 text-[7px] font-bold text-rose-400">NO IMG</div>}
                      <div>
                        <p className="font-medium text-slate-200">{p.name}</p>
                        <p className="font-mono text-[10px] text-slate-500">{p.sku}</p>
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {p.product_type === "AGENT_PRODUCT" ? (
                            <span data-testid={`agent-owned-badge-${p.sku}`} className="rounded bg-purple-500/15 px-1.5 py-0.5 text-[9px] font-bold text-purple-400">AGENT PRODUCT{agentNames[p.owner_agent_id] ? ` · ${agentNames[p.owner_agent_id]}` : ""}</span>
                          ) : (
                            <span data-testid={`admin-owned-badge-${p.sku}`} className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[9px] font-bold text-sky-400">ADMIN PRODUCT</span>
                          )}
                          {p.approval_status === "PENDING" && <span data-testid={`pending-badge-${p.sku}`} className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold text-amber-400">PENDING APPROVAL</span>}
                          {p.approval_status === "REJECTED" && <span data-testid={`rejected-badge-${p.sku}`} className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[9px] font-bold text-rose-400">REJECTED</span>}
                          {p.approval_status === "SUSPENDED" && <span data-testid={`suspended-badge-${p.sku}`} className="rounded bg-slate-500/15 px-1.5 py-0.5 text-[9px] font-bold text-slate-400">SUSPENDED</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-400">{p.category || "—"}</td>
                  <td className="px-3 py-2 text-slate-300">{fmtINR(p.agent_cost)}</td>
                  <td className="px-3 py-2 text-slate-300">{fmtINR(p.suggested_price)}</td>
                  <td className="px-3 py-2 text-amber-400">{fmtINR(p.min_price)}</td>
                  <td className="px-3 py-2">
                    <span className={p.stock - (p.reserved || 0) <= p.reorder_level ? "font-semibold text-amber-400" : "text-slate-300"}>
                      {p.stock - (p.reserved || 0)}
                    </span>
                    <span className="text-slate-600"> / {p.stock}</span>
                  </td>
                  <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1.5">
                      {p.approval_status === "PENDING" && (
                        <>
                          <button data-testid={`approve-product-${p.sku}`} onClick={() => approveMut.mutate(p.id)}
                            className="rounded bg-emerald-500/10 px-2 py-1 text-[10px] font-bold text-emerald-400 hover:bg-emerald-500/20">Approve</button>
                          <button data-testid={`reject-product-${p.sku}`} onClick={() => { const reason = window.prompt("Rejection reason (shown to the agent):"); if (reason) rejectMut.mutate({ id: p.id, reason }); }}
                            className="rounded bg-rose-500/10 px-2 py-1 text-[10px] font-bold text-rose-400 hover:bg-rose-500/20">Reject</button>
                        </>
                      )}
                      {p.approval_status === "SUSPENDED" && (
                        <button data-testid={`enable-product-${p.sku}`} onClick={() => approveMut.mutate(p.id)}
                          className="rounded bg-emerald-500/10 px-2 py-1 text-[10px] font-bold text-emerald-400 hover:bg-emerald-500/20">Enable</button>
                      )}
                      <button data-testid={`edit-product-${p.sku}`} onClick={() => setDialog({ mode: "edit", product: p })}
                        className="rounded p-1 text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-200"><Pencil size={13} /></button>
                      <button data-testid={`assign-product-${p.sku}`} onClick={() => setDialog({ mode: "assign", product: p })}
                        className="rounded p-1 text-slate-500 transition-colors hover:bg-slate-800 hover:text-orange-400"><UserPlus size={13} /></button>
                      <button data-testid={`archive-product-${p.sku}`} onClick={() => archive.mutate(p.id)} title="Archive (hide, keep history)"
                        className="rounded p-1 text-slate-500 transition-colors hover:bg-slate-800 hover:text-rose-400"><Archive size={13} /></button>
                      <button data-testid={`delete-product-${p.sku}`} onClick={() => setDialog({ mode: "delete", product: p })} title="Delete permanently"
                        className="rounded p-1 text-slate-500 transition-colors hover:bg-rose-500/10 hover:text-rose-500"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pager page={data.page} pages={data.pages} onPage={setPage} />}

      <Dialog open={dialog?.mode === "create" || dialog?.mode === "edit"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-2xl border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader><DialogTitle>{dialog?.mode === "edit" ? `Edit ${dialog.product.sku}` : "New Product"}</DialogTitle></DialogHeader>
          {dialog && (
            <ProductForm
              initial={dialog.mode === "edit" ? { ...EMPTY, ...dialog.product } : EMPTY}
              categories={categories || []}
              busy={save.isPending}
              onSave={(payload) => save.mutate({ mode: dialog.mode, payload, id: dialog.product?.id })}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.mode === "delete"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader><DialogTitle>Delete {dialog?.product?.name} permanently?</DialogTitle></DialogHeader>
          {dialog?.product && (
            <div className="space-y-3 text-xs">
              <p className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-rose-300" data-testid="delete-warning">
                This removes <b>{dialog.product.name}</b> ({dialog.product.sku}) and its agent basket/store/marketplace selections everywhere. This cannot be undone.
              </p>
              <p className="text-slate-400">If the product has any orders, sales or live marketplace listings, deletion is blocked — use <b>Archive</b> instead to hide it while preserving history.</p>
              <div className="flex gap-2">
                <Button data-testid="delete-confirm-btn" onClick={() => hardDelete.mutate(dialog.product.id)} disabled={hardDelete.isPending}
                  className="flex-1 bg-rose-600 font-semibold text-white hover:bg-rose-500">
                  <Trash2 size={13} /> {hardDelete.isPending ? "Deleting…" : "Delete permanently"}
                </Button>
                <Button data-testid="delete-cancel-btn" variant="outline" onClick={() => setDialog(null)}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.mode === "import"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader><DialogTitle>Bulk Product Import</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-400">Upload a CSV or XLSX with columns: sku, name, brand, category, purchase_cost, agent_cost, mrp, suggested_price, min_price, gst, hsn, weight, stock, images, tags.</p>
            <a data-testid="download-template-link" href={`${BACKEND}/api/admin/products/import-template/csv`}
              className="inline-flex items-center gap-1.5 text-xs text-orange-400 hover:text-orange-300"><Download size={12} /> Download CSV template</a>
            <Button data-testid="import-choose-file-btn" variant="outline" className="w-full border-dashed border-slate-600 py-8 text-slate-300"
              onClick={() => importRef.current.click()}>
              <Upload size={16} className="mr-2" /> Choose CSV / XLSX file
            </Button>
            <input ref={importRef} type="file" accept=".csv,.xlsx" className="hidden" data-testid="import-file-input"
              onChange={(e) => e.target.files[0] && doImport(e.target.files[0])} />
            {importResult && (
              <div data-testid="import-result" className="rounded-lg border border-slate-700 bg-slate-900 p-4 text-xs">
                <p className="text-slate-200">Total: <b>{importResult.total}</b> · Imported: <b className="text-emerald-400">{importResult.imported}</b> · Failed: <b className="text-rose-400">{importResult.failed}</b></p>
                {importResult.errors.length > 0 && (
                  <>
                    <div className="mt-2 max-h-32 space-y-1 overflow-y-auto">
                      {importResult.errors.slice(0, 10).map((e, i) => (
                        <p key={i} className="text-rose-400">Row {e.row} ({e.sku || "—"}): {e.error}</p>
                      ))}
                    </div>
                    <Button data-testid="download-errors-btn" size="sm" variant="outline" onClick={downloadErrors}
                      className="mt-2 border-slate-600 text-slate-300"><Download size={12} /> Error report</Button>
                  </>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.mode === "assign"} onOpenChange={() => { setDialog(null); setAssignAgents([]); }}>
        <DialogContent className="border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader>
            <DialogTitle>Assign {dialog?.product ? `"${dialog.product.name}"` : `${selected.length} products`} to agents</DialogTitle>
          </DialogHeader>
          <div className="space-y-2" data-testid="assign-agent-list">
            {(agents?.items || []).map((a) => (
              <label key={a.id} data-testid={`assign-agent-${a.id}`} className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2.5">
                <Checkbox checked={assignAgents.includes(a.id)}
                  onCheckedChange={(c) => setAssignAgents(c ? [...assignAgents, a.id] : assignAgents.filter((x) => x !== a.id))} />
                <div>
                  <p className="text-sm text-slate-200">{a.business_name}</p>
                  <p className="text-[10px] text-slate-500">{a.name} · {a.email}</p>
                </div>
              </label>
            ))}
          </div>
          <Button data-testid="assign-confirm-btn" disabled={!assignAgents.length || doAssign.isPending} onClick={() => doAssign.mutate()}
            className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600">Assign products</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
